<template>
  <base-wrapper :loading="loadingCurentPeriod || loadingOrder">
    <div class="space-y-4">
      <div v-if="filter.isOmzet" class="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-6">
        <BaseWidget label="Omzet" :value="orders.meta.total_price" />
      </div>
      <div class="flex items-center justify-end gap-2">
        <BaseCheckbox label="Omzet" v-model="filter.isOmzet" @change="loadOrders" />
        <period-select
          class="gap-2"
          v-model="filter.periodId"
          @change="loadOrders"
        />
        <base-select
          v-if="!filter.isOmzet"
          class="w-48"
          :shadow="false"
          :options="filterOrderStatusOptions"
          v-model="filter.orderStatus"
          @change="loadOrders"
        />
        <base-input
          type="search"
          :shadow="false"
          placeholder="Cari Kode atau Nama"
          debounce
          v-model="filter.search"
          @native-input="loadOrders"
        />
        <base-button :to="{ name: 'penjualan.tambah' }">
          <Icon icon="heroicons:plus-solid" class="h-4 w-4" />
          Tambah Transaksi
        </base-button>
      </div>

      <datatable
        :columns="tableColumns"
        :total="orders.meta.page.total"
        :perPage="orders.meta.page.perPage"
        :currentPage="orders.meta.page.currentPage"
        :meta="orders.meta"
        @pagechanged="onChangePage"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="order in orders.data"
            :key="order.id"
            :class="[classes.tr, 'cursor-pointer bg-white hover:bg-green-100']"
            @click="onClickDetail(order.attributes.order_id)"
          >
            <td :class="[classes.td, 'space-y-1']">
              <order-code-label :order="order" />
              <order-date-label :order="order" class="text-xs text-gray-500" />
            </td>
            <td :class="[classes.td]">
              {{
                getSingleIncluded(orders, order.relationships['area'].data.id)
                  .attributes.name
              }}
              ({{
                getSingleIncluded(orders, order.relationships['area'].data.id)
                  .attributes.code
              }})
            </td>
            <td :class="[classes.td]">-</td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(orders, order.relationships.order.data.id)
                  .attributes.grand_total_pv | toCurrency
              }}
            </td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(orders, order.relationships.order.data.id)
                  .attributes.total_bonus_value | toCurrency
              }}
            </td>
            <td :class="[classes.td, 'text-center']" :title="getSingleIncluded(orders, order.relationships.order.data.id).attributes.latest_cancel_description">
              <base-badge
                v-if="order.attributes.order_status === 'Draft'"
                color="yellow"
                >Draft</base-badge
              >
              <base-badge v-else color="green">Valid</base-badge>
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                v-if="
                  order.attributes.warehouse_status === 'Belum Masuk Gudang'
                "
                color="yellow"
                >Kasir</base-badge
              >
              <base-badge v-else color="indigo">{{
                order.attributes.warehouse_status
              }}</base-badge>
            </td>
            <td :class="[classes.td, 'text-center']">
              <base-badge
                :color="getOrderReportStatus(order).color"
                >{{
                  getOrderReportStatus(order).text
                }}</base-badge
              >
            </td>
            <td :class="[classes.td, 'text-right']">
              {{
                getSingleIncluded(orders, order.relationships['order'].data.id)
                  .attributes.grand_total_price | toCurrency
              }}
              <span
                v-if="
                  getSingleIncluded(
                    orders,
                    order.relationships['order'].data.id
                  ).attributes.grand_total_price !==
                  getSingleIncluded(
                    orders,
                    order.relationships['order'].data.id
                  ).attributes.grand_total_report_price
                "
                class="text-red-600"
                >*</span
              >
            </td>
          </tr>
        </template>
        <template v-if="orders.data.length" #tfoot="{ classes }">
          <tfoot>
            <tr :class="classes.tr">
              <td colspan="7"></td>
              <td :class="[classes.td, 'pb-0 text-right']">Total Penjualan</td>
              <td :class="[classes.td, 'pb-0 text-right']">
                <strong>{{ summary.totalOrderPrice | toCurrency }}</strong>
              </td>
            </tr>
            <tr :class="classes.tr">
              <td colspan="7"></td>
              <td :class="[classes.td, 'pb-0 text-right']">
                Total Nilai Wilayah
              </td>
              <td :class="[classes.td, 'pb-0 text-right']">
                <strong>{{ summary.totalReportPrice | toCurrency }}</strong>
              </td>
            </tr>
            <tr :class="classes.tr">
              <td colspan="7"></td>
              <td :class="[classes.td, 'pb-0 text-right']">Selisih</td>
              <td :class="[classes.td, 'pb-0 text-right']">
                <strong>{{ summary.priceGap | toCurrency }}</strong>
              </td>
            </tr>
            <tr :class="classes.tr">
              <td colspan="7"></td>
              <td :class="[classes.td, 'text-right']">Total Yang Dibayarkan</td>
              <td :class="[classes.td, 'text-right']">
                <strong>{{ summary.totalReportPrice | toCurrency }}</strong>
              </td>
            </tr>
          </tfoot>
        </template>
      </datatable>
    </div>

    <view-order-modal
      label="Penjualan"
      with-bonus
      with-indent
      printable
      edit-route-name="penjualan.edit"
      :columns="[
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'address',
      ]"
      :order-detail-columns="['code', 'qty', 'price', 'total_price', 'packed_qty', 'unpacked_qty']"
      :visible="detailModal.visible"
      :order-id="detailModal.orderId"
      @close="detailModal.visible = false"
      @validated="onRefresh"
      @deleted="onRefresh"
    />
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapGetters } from 'vuex';
import ViewOrderModal from '@/components/order/view-order-modal.vue';
import OrderCodeLabel from '@/components/order/order-code-label.vue';
import OrderDateLabel from '@/components/order/order-date-label.vue';
import PeriodSelect from '@/components/period/period-select.vue';
import BaseCheckbox from '../../../../components/base/BaseCheckbox.vue';
import BaseWidget from '../../../../components/base/BaseWidget.vue';

export default {
  mixins: [requestMixin],
  components: {
    ViewOrderModal,
    OrderCodeLabel,
    OrderDateLabel,
    PeriodSelect,
    BaseCheckbox,
    BaseWidget
  },
  data() {
    return {
      filter: {
        search: null,
        periodId: null,
        orderStatus: null,
        isOmzet: false
      },
      detailModal: {
        orderId: null,
        visible: false,
      },
      loadingCurentPeriod: false,
      loadingOrder: false,
      orders: {
        data: [],
        meta: {
          page: {},
        },
      },
      summary: {
        totalOrderPrice: 0,
        totalReportPrice: 0,
        priceGap: 0,
      },
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
    }),
    tableColumns() {
      return [
        { id: 'code', name: 'Nomor Faktur' },
        { id: 'office_code', name: 'Wilayah Harga' },
        { id: 'description', name: 'Keterangan' },
        { id: 'point', name: 'PV', theadClass: 'text-right' },
        { id: 'bonus', name: 'BV', theadClass: 'text-right' },
        {
          id: 'order_status',
          name: 'Status Faktur',
          theadClass: 'text-center',
        },
        {
          id: 'warehouse_status',
          name: 'Status Barang',
          theadClass: 'text-center',
        },
        {
          id: 'report_status',
          name: 'Status Laporan',
          theadClass: 'text-center',
        },
        {
          id: 'belanja',
          name: 'Total Harga',
          theadClass: 'text-right',
        },
      ];
    },
    filterOrderStatusOptions() {
      return [
        {
          key: 'null',
          label: 'Semua',
          value: null,
        },
        {
          key: 'valid',
          label: 'Valid',
          value: 'valid',
        },
        {
          key: 'draft',
          label: 'Draft',
          value: 'draft',
        },
        {
          key: 'cancel',
          label: 'Batal Faktur',
          value: 'cancel',
        },
        {
          key: 'hapus',
          label: 'Hapus',
          value: 'hapus',
        },
        {
          key: 'ada_susulan',
          label: 'Ada Susulan',
          value: 'ada_susulan',
        },
        {
          key: 'ada_reimbursement',
          label: 'Ada Reimbursement',
          value: 'ada_reimbursement',
        },
        {
          key: 'ada_susulan_promo',
          label: 'Ada Susulan Promo',
          value: 'ada_susulan_promo',
        },
      ];
    },
    filterOrderStatus() {
      if (this.filter.isOmzet) {
        return {
          'filter[order_status]': 'Terlaporkan'
        }
      }

      if (
        this.filter.orderStatus === null ||
        this.filter.orderStatus === 'hapus'
      ) {
        return null;
      }

      if (
        this.filter.orderStatus === 'ada_susulan'
      ) {
        return {
          'filter[order_status]': 'Ada Susulan,Susulan Dikirim',
        }
      }

      if (
        this.filter.orderStatus === 'ada_reimbursement'
      ) {
        return {
          'filter[need_reimbursement]': true,
        }
      }

      if (
        this.filter.orderStatus === 'ada_susulan_promo'
      ) {
        return {
          'filter[need_free_reimbursement]': true,
        }
      }

      if (this.filter.orderStatus === 'valid') {
        return {
          'filter[not-order_status]':
            'Draft,Batal Faktur Gudang,Batal Faktur Kasir,Terhapus',
        };
      }

      if (this.filter.orderStatus === 'draft') {
        return {
          'filter[order_status]': 'Draft',
        };
      }

      return {
        'filter[order_status]': 'Batal Faktur Gudang,Batal Faktur Kasir',
      };
    },
  },
  methods: {
    getOrderReportStatus(simpleOrder) {
      const order = this.getSingleIncluded(this.orders, simpleOrder.relationships.order.data.id)

      if (!order.attributes.has_bundle) {
        return {
          color: 'yellow',
          text: 'Belum Dilaporan'
        }
      }

      if (order.attributes.order_status === 'Terlaporkan') {
        return {
          color: 'green',
          text: 'Dilaporan Omzet'
        }
      }

      return {
        color: 'blue',
        text: 'Dilaporan Penjualan'
      }
    },
    async loadCurrentPeriod() {
      this.loadingCurentPeriod = true;

      const [res, err] = await this.request(
        '/api/v1/periods/-actions/get-current-period'
      );

      if (!err) {
        this.filter.periodId = res.data.id;
      }

      this.loadingCurentPeriod = false;
    },
    async loadOrders(params) {
      this.loadingOrder = true;

      const [res, err] = await this.request('/api/v1/orders', {
        params: {
          'page[size]': 5,
          include: 'order,area',
          'fields[simple-orders]':
            'order,order_type,origin_code,destination_code,updatedAt,order_status,order_id,warehouse_status,createdAt,area',
          'fields[offices]': 'code',
          'fields[orders]': 'grand_total_price,grand_total_report_price,grand_total_pv,total_bonus_value,has_bundle,order_status,latest_cancel_description',
          'fields[periods]': 'start_date',
          'filter[destination_office_category_id]': 4,
          'filter[origin_warehouse_id]': this.me.current_warehouse,
          'filter[search]': this.filter.search,
          [`filter[${this.filter.isOmzet ? 'omzet_period_uuid' : 'period_uuid'}]`]: this.filter.periodId,
          'filter[order_type]': 'sale',
          'filter[with_summary]': true,
          ...this.filterOrderStatus,
          ...params,
        },
      });

      if (!err) {
        this.orders = res;

        if (res.meta.total_price) {
          this.summary.totalOrderPrice = res.meta.total_price;
          this.summary.totalReportPrice = res.meta.total_report_price;
          this.summary.priceGap = res.meta.price_gap;
        }
      }

      this.loadingOrder = false;
    },
    onChangePage(page) {
      this.loadOrders({
        'page[number]': page,
      });
    },
    onClickDetail(orderId) {
      this.detailModal.orderId = orderId;
      this.detailModal.visible = true;
    },
    onRefresh() {
      this.detailModal.visible = false;

      this.loadOrders();
    },
  },
  async created() {
    await this.loadCurrentPeriod();

    this.loadOrders();
  },
};
</script>
